/**
 * Shared parsing for the paywall error codes the server returns (ROO-82) when
 * a student hits the free→paid boundary:
 *   - 402 { code: 'free_limit_reached', data: { freeTestsUsed, freeTestsLimit } }
 *     when the 3 free full practice tests have all been used.
 *   - 402 { code: 'subscription_required' } on premium-only surfaces.
 *
 * Both TestRunner.tsx and TestTaking.tsx call `getPaywallError` on their
 * `/tests/:id/start` catch handler so the two surfaces render the same
 * upsell UI instead of a raw "Failed to load test" message.
 */

export type PaywallErrorCode = 'free_limit_reached' | 'subscription_required';

export interface PaywallError {
  code: PaywallErrorCode;
  data?: {
    freeTestsUsed?: number;
    freeTestsLimit?: number;
    [key: string]: unknown;
  };
}

const PAYWALL_CODES: PaywallErrorCode[] = ['free_limit_reached', 'subscription_required'];

/**
 * Returns the paywall error payload if `err` is an axios error carrying a 402
 * response with one of the known paywall codes; otherwise null (so callers
 * can fall back to their normal error handling for every other failure).
 */
export function getPaywallError(err: unknown): PaywallError | null {
  const response = (err as { response?: { status?: number; data?: unknown } } | null)?.response;
  if (!response || response.status !== 402) return null;

  const body = response.data as { code?: string; data?: PaywallError['data'] } | undefined;
  const code = body?.code;
  if (!code || !PAYWALL_CODES.includes(code as PaywallErrorCode)) return null;

  return { code: code as PaywallErrorCode, data: body?.data };
}
